/*
 * ECHO Coffee Roasters theme — vanilla JS, no dependencies.
 *
 * Replaces the React storefront's client code:
 *   cartStore.ts / CartDrawer.tsx  -> Cart + Drawer   (Shopify Ajax Cart API)
 *   mugOffer.ts / MugUpsellDialog  -> Mug              (welcome-price swap + dialogs)
 *   product.$handle.tsx            -> initProduct      (variants, Coffee Club plans, price)
 *   ui/carousel.tsx                -> initCarousel
 *   AnnouncementBar.tsx            -> initAnnouncements
 *
 * Shopify owns the cart; this file only calls its endpoints and re-renders the
 * cart-drawer section from the server after each change.
 */
(() => {
  'use strict';

  const cfg = window.echo;
  if (!cfg) return;

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));

  const currency = new Intl.NumberFormat(cfg.locale, { style: 'currency', currency: cfg.currency });
  const formatMoney = (cents) => currency.format(cents / 100);

  const store = {
    get(key) {
      try {
        return sessionStorage.getItem(key);
      } catch {
        return null;
      }
    },
    set(key, value) {
      try {
        sessionStorage.setItem(key, value);
      } catch {
        /* storage unavailable (private mode) — offers may repeat, which is harmless */
      }
    },
  };

  /** Close a <dialog> when the click lands outside its box (i.e. on the backdrop). */
  function closeOnBackdropClick(dialog, onClose) {
    dialog.addEventListener('click', (event) => {
      if (event.target !== dialog) return;
      const rect = dialog.getBoundingClientRect();
      const inside =
        event.clientX >= rect.left &&
        event.clientX <= rect.right &&
        event.clientY >= rect.top &&
        event.clientY <= rect.bottom;
      if (!inside) {
        dialog.close();
        if (onClose) onClose();
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /* Cart (Shopify Ajax API)                                             */
  /* ------------------------------------------------------------------ */

  const api = {
    async request(url, options = {}) {
      const response = await fetch(url, {
        headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
        ...options,
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.description || data.message || 'Something went wrong. Please try again.');
      }
      return data;
    },
    getCart: () => api.request(cfg.routes.cartJson),
    add: (item) => api.request(cfg.routes.cartAdd, { method: 'POST', body: JSON.stringify({ items: [item] }) }),
    change: (line, quantity) =>
      api.request(cfg.routes.cartChange, { method: 'POST', body: JSON.stringify({ line, quantity }) }),
    clear: () => api.request(cfg.routes.cartClear, { method: 'POST', body: '{}' }),
  };

  const Drawer = {
    el: null,
    lastFocus: null,

    init() {
      this.el = $('[data-cart-drawer]');
    },

    open() {
      if (!this.el || this.el.dataset.open === 'true') return;
      this.lastFocus = document.activeElement;
      this.el.classList.remove('hidden');
      // two frames so the browser paints the closed state before the slide-in starts
      requestAnimationFrame(() => requestAnimationFrame(() => (this.el.dataset.open = 'true')));
      document.body.style.overflow = 'hidden';
    },

    close() {
      if (!this.el || this.el.dataset.open !== 'true') return;
      this.el.dataset.open = 'false';
      document.body.style.overflow = '';
      setTimeout(() => {
        if (this.el.dataset.open === 'false') this.el.classList.add('hidden');
      }, 250);
      if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
    },

    setBusy(busy) {
      const inner = $('[data-cart-drawer-inner]', this.el);
      if (!inner) return;
      inner.classList.toggle('pointer-events-none', busy);
      inner.classList.toggle('opacity-70', busy);
      inner.setAttribute('aria-busy', String(busy));
    },
  };

  const Cart = {
    hadSubscription: false,

    updateCount(count) {
      $$('[data-cart-count]').forEach((el) => {
        el.textContent = count;
        el.classList.toggle('hidden', count === 0);
      });
    },

    /** Re-render the drawer from the server (Section Rendering API). */
    async renderDrawer() {
      const response = await fetch(`${cfg.routes.root}?sections=cart-drawer`);
      const sections = await response.json();
      const fresh = new DOMParser()
        .parseFromString(sections['cart-drawer'], 'text/html')
        .querySelector('[data-cart-drawer-inner]');
      const current = $('[data-cart-drawer-inner]');
      if (fresh && current) current.innerHTML = fresh.innerHTML;
    },

    /**
     * Sync everything after a cart change.
     * opts.open  - slide the drawer open (and offer the mug if a subscription is in the cart)
     */
    async refresh(opts = {}) {
      try {
        let cart = await api.getCart();
        cart = await Mug.syncPricing(cart);
        await this.renderDrawer();
        this.updateCount(cart.item_count);

        const hasSubscription = cart.items.some((item) => item.selling_plan_allocation);
        const justAdded = hasSubscription && !this.hadSubscription;
        this.hadSubscription = hasSubscription;

        if (opts.open) Drawer.open();
        Mug.maybeOffer(cart, { justAdded, opened: !!opts.open });
      } catch (error) {
        console.error('Cart refresh failed:', error);
      }
    },

    async mutate(action) {
      Drawer.setBusy(true);
      try {
        await action();
      } catch (error) {
        console.error(error);
      }
      await this.refresh();
      Drawer.setBusy(false);
    },
  };

  /* ------------------------------------------------------------------ */
  /* Coffee Club mug offer                                               */
  /* ------------------------------------------------------------------ */

  const Mug = {
    config: null,
    syncing: false,

    init() {
      const el = $('[data-mug-config]');
      if (!el) return;
      try {
        this.config = JSON.parse(el.textContent);
      } catch {
        return;
      }

      const upsell = $('[data-mug-upsell]');
      if (upsell) {
        upsell.addEventListener('close', () => this.dismiss());
        $('[data-mug-dismiss]', upsell).addEventListener('click', () => upsell.close());
        $('[data-mug-add]', upsell).addEventListener('click', async (event) => {
          const button = event.currentTarget;
          button.disabled = true;
          try {
            await api.add({ id: this.config.welcomeVariantId, quantity: 1 });
            upsell.close();
            await Cart.refresh({ open: true });
          } catch (error) {
            console.error('Failed to add mug:', error);
          } finally {
            button.disabled = false;
          }
        });
      }
    },

    dismissed: () => store.get('echo-mug-offer-dismissed') === '1',
    dismiss: () => store.set('echo-mug-offer-dismissed', '1'),

    showUpsell() {
      const dialog = $('[data-mug-upsell]');
      if (this.config && dialog && !dialog.open && !this.dismissed()) dialog.showModal();
    },

    /** Offer the mug once, when a subscription is in the cart and the mug is not. */
    maybeOffer(cart, { justAdded, opened }) {
      if (!this.config || this.dismissed()) return;
      const hasSubscription = cart.items.some((item) => item.selling_plan_allocation);
      const hasMug = cart.items.some((item) => item.handle === this.config.handle);
      if ((justAdded || opened) && hasSubscription && !hasMug) this.showUpsell();
    },

    /**
     * Keeps the mug line priced correctly: the discounted "Welcome" variant while a
     * subscription is in the cart, the regular variant when it isn't.
     */
    async syncPricing(cart) {
      if (!this.config || this.syncing) return cart;

      const index = cart.items.findIndex((item) => item.handle === this.config.handle);
      if (index === -1) return cart;

      const line = cart.items[index];
      const hasSubscription = cart.items.some((item) => item.selling_plan_allocation);
      const isWelcome = line.variant_id === this.config.welcomeVariantId;
      if (hasSubscription === isWelcome) return cart;

      const target = hasSubscription ? this.config.welcomeVariantId : this.config.regularVariantId;
      this.syncing = true;
      try {
        await api.change(index + 1, 0);
        await api.add({ id: target, quantity: line.quantity });
        return await api.getCart();
      } catch (error) {
        console.error('Failed to sync mug offer pricing:', error);
        return await api.getCart();
      } finally {
        this.syncing = false;
      }
    },

    /** Timed promo popup on the mug's own product page. */
    initPopup() {
      const popup = $('[data-mug-popup]');
      if (!popup || !this.config || !this.config.popupEnabled) return;
      if (store.get('echo-mug-deal-popup-dismissed') === 'true') return;

      const dismiss = () => store.set('echo-mug-deal-popup-dismissed', 'true');
      popup.addEventListener('close', dismiss);
      $$('[data-mug-popup-close]', popup).forEach((el) => el.addEventListener('click', () => popup.close()));
      closeOnBackdropClick(popup);

      setTimeout(() => {
        if (!popup.open) popup.showModal();
      }, this.config.popupDelay);
    },
  };

  /* ------------------------------------------------------------------ */
  /* Product page                                                        */
  /* ------------------------------------------------------------------ */

  function initProduct(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';

    const data = JSON.parse($('[data-product-json]', root).textContent);
    const form = $('[data-product-form]', root);
    if (!form) return;

    const idInput = $('input[name="id"]', form);
    const planInput = $('[data-selling-plan-input]', form);
    const addButton = $('[data-add-button]', form);
    const errorEl = $('[data-product-error]', form);
    const priceEl = $('[data-price]', root);
    const compareEl = $('[data-compare-price]', root);
    const planNameEl = $('p [data-plan-name]', root);
    const planPanel = $('[data-plan-panel]', root);
    const variantInputs = $$('[data-variant-input]', form);
    const purchaseInputs = $$('[data-purchase-input]', form);
    const planInputs = $$('[data-plan-input]', form);

    const state = {
      variantId: Number(idInput.value) || (data.variants[0] && data.variants[0].id),
      purchase: 'one-time',
      planId: (planInputs.find((input) => input.checked) || {}).value || null,
    };

    function update() {
      const variant = data.variants.find((v) => v.id === state.variantId) || data.variants[0];
      if (!variant) return;

      const plan = state.purchase === 'club' && state.planId ? variant.allocations[state.planId] : null;
      const isSubscription = !!plan;
      const unitPrice = isSubscription ? plan.price : variant.price;
      const strikePrice = isSubscription ? variant.price : variant.compare > variant.price ? variant.compare : 0;

      idInput.value = variant.id;
      planInput.disabled = !isSubscription;
      planInput.value = isSubscription ? state.planId : '';

      priceEl.textContent = formatMoney(unitPrice);
      compareEl.textContent = strikePrice ? formatMoney(strikePrice) : '';
      compareEl.classList.toggle('hidden', !strikePrice);

      if (planNameEl) {
        const selected = planInputs.find((input) => input.value === state.planId);
        planNameEl.textContent = isSubscription && selected ? selected.dataset.planLabel : '';
        planNameEl.classList.toggle('hidden', !isSubscription);
      }
      if (planPanel) planPanel.classList.toggle('hidden', state.purchase !== 'club');

      const label = addButton.dataset.label || variant.title;
      addButton.disabled = !variant.available;
      addButton.textContent = variant.available
        ? `${isSubscription ? 'Subscribe' : 'Add'} ${label} — ${formatMoney(unitPrice)}${isSubscription ? ' / delivery' : ''}`
        : 'Sold out';
    }

    variantInputs.forEach((input) =>
      input.addEventListener('change', () => {
        state.variantId = Number(input.value);
        update();
      }),
    );

    purchaseInputs.forEach((input) =>
      input.addEventListener('change', () => {
        if (!input.checked) return;
        state.purchase = input.value;
        update();
        // The $ mug is the incentive to subscribe, so offer it as soon as someone considers the Club.
        if (state.purchase === 'club') Mug.showUpsell();
      }),
    );

    planInputs.forEach((input) =>
      input.addEventListener('change', () => {
        state.planId = input.value;
        update();
      }),
    );

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      errorEl.classList.add('hidden');
      addButton.disabled = true;
      addButton.textContent = 'Adding...';

      const item = { id: Number(idInput.value), quantity: 1 };
      if (!planInput.disabled && planInput.value) item.selling_plan = Number(planInput.value);

      try {
        await api.add(item);
        await Cart.refresh({ open: true });
      } catch (error) {
        errorEl.textContent = error.message;
        errorEl.classList.remove('hidden');
      } finally {
        update();
      }
    });

    // Gallery thumbnails
    const mainImage = $('[data-main-image] img', root);
    $$('[data-thumb]', root).forEach((thumb) =>
      thumb.addEventListener('click', () => {
        if (mainImage) {
          mainImage.src = thumb.dataset.src;
          mainImage.srcset = thumb.dataset.srcset;
          mainImage.alt = thumb.dataset.alt;
        }
        $$('[data-thumb]', root).forEach((other) => {
          const active = other === thumb;
          other.setAttribute('aria-current', String(active));
          other.classList.toggle('opacity-100', active);
          other.classList.toggle('opacity-60', !active);
        });
      }),
    );

    if (data.isMug) Mug.initPopup();
    update();
  }

  /* ------------------------------------------------------------------ */
  /* Carousel, announcements, mobile nav                                 */
  /* ------------------------------------------------------------------ */

  function initCarousel(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';

    const track = $('[data-carousel-track]', root);
    const step = () => (track.firstElementChild ? track.firstElementChild.getBoundingClientRect().width : track.clientWidth);
    const atEnd = () => track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;

    const next = $('[data-carousel-next]', root);
    const prev = $('[data-carousel-prev]', root);
    if (next) next.addEventListener('click', () => (atEnd() ? track.scrollTo({ left: 0 }) : track.scrollBy({ left: step() })));
    if (prev)
      prev.addEventListener('click', () =>
        track.scrollLeft <= 4 ? track.scrollTo({ left: track.scrollWidth }) : track.scrollBy({ left: -step() }),
      );

    track.addEventListener('keydown', (event) => {
      if (event.key === 'ArrowRight') track.scrollBy({ left: step() });
      if (event.key === 'ArrowLeft') track.scrollBy({ left: -step() });
    });
  }

  function initAnnouncements(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = 'true';

    const items = $$('[data-announcement]', root);
    if (items.length < 2) return;

    let index = 0;
    setInterval(() => {
      items[index].classList.add('hidden');
      index = (index + 1) % items.length;
      items[index].classList.remove('hidden');
    }, Number(root.dataset.interval) || 8000);
  }

  function initMobileNav(toggle) {
    if (toggle.dataset.ready) return;
    toggle.dataset.ready = 'true';

    const nav = document.getElementById(toggle.getAttribute('aria-controls'));
    toggle.addEventListener('click', () => {
      const open = nav.classList.contains('hidden');
      nav.classList.toggle('hidden', !open);
      nav.classList.toggle('flex', open);
      toggle.setAttribute('aria-expanded', String(open));
      toggle.textContent = open ? toggle.dataset.labelClose : toggle.dataset.labelOpen;
    });
  }

  /* ------------------------------------------------------------------ */
  /* Boot                                                                */
  /* ------------------------------------------------------------------ */

  function initSections() {
    $$('[data-product-root]').forEach(initProduct);
    $$('[data-carousel]').forEach(initCarousel);
    $$('[data-announcement-bar]').forEach(initAnnouncements);
    $$('[data-mobile-nav-toggle]').forEach(initMobileNav);
  }

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-cart-open], [data-cart-close], [data-cart-qty], [data-cart-clear]');
    if (!target) return;

    if (target.matches('[data-cart-open]')) {
      event.preventDefault();
      Drawer.open();
      Cart.refresh({ open: true });
    } else if (target.matches('[data-cart-close]')) {
      Drawer.close();
    } else if (target.matches('[data-cart-qty]')) {
      Cart.mutate(() => api.change(Number(target.dataset.line), Number(target.dataset.quantity)));
    } else if (target.matches('[data-cart-clear]')) {
      Cart.mutate(() => api.clear());
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') Drawer.close();
  });

  // Theme editor: re-run initialisers when sections are added or edited.
  document.addEventListener('shopify:section:load', initSections);

  function boot() {
    Drawer.init();
    Mug.init();
    initSections();
    // Make sure the mug is priced correctly even if the cart changed in another tab.
    api
      .getCart()
      .then(async (cart) => {
        Cart.hadSubscription = cart.items.some((item) => item.selling_plan_allocation);
        const synced = await Mug.syncPricing(cart);
        if (synced !== cart) Cart.refresh();
      })
      .catch(() => undefined);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
